import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "../utils/structuredData";

type JsonLd = Record<string, unknown>;

interface SeoProps {
  title: string;
  description: string;
  /** Absolute or root-relative image URL for social cards. */
  image?: string;
  /** Set on pages that should not be indexed (auth, account, etc.). */
  noindex?: boolean;
  /** One or more schema.org JSON-LD graphs to emit for this page. */
  jsonLd?: JsonLd | JsonLd[];
}

// Presentational metadata component. Pages build their own title/description
// (from vertical content or a sensible default) and pass it here; this owns the
// canonical URL, Open Graph, Twitter, and JSON-LD boilerplate. react-snap bakes
// the result into each prerendered page at build time.
const Seo: React.FC<SeoProps> = ({ title, description, image = DEFAULT_OG_IMAGE, noindex, jsonLd }) => {
  const { pathname } = useLocation();
  const canonicalPath = pathname.replace(/\/+$/, "") || "/";
  const canonical = `${SITE_URL}${canonicalPath}`;
  const imageUrl = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  const graphs = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {graphs.map((graph, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(graph)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
